/**
 * Pure geometry + formatting helpers for the unified select / pause
 * mode (select-to-chat, `docs/design/preview-select-to-chat-prd.md`).
 *
 * Everything here is pure and unit-tested
 * (`__tests__/select-area.test.ts`). The DOM/pointer halves live in the
 * injected inspector (per iframe) and the renderer hook
 * (`usePreviewInspector`), which stay thin.
 *
 * Coordinate spaces (mixing them is THE bug class this module guards):
 *   - INSPECTOR space — CSS px in a preview iframe's own viewport (what
 *     `getBoundingClientRect()` returns inside that iframe). Every
 *     selection rect crosses the bridge in this space, tagged with the
 *     iframe's `viewport` size.
 *   - WINDOW space — Foundry-window CSS px, what `preview:capture-area`
 *     wants. `scalePaneRect` maps inspector→display-box px; the caller
 *     adds the iframe's client offset.
 *   - SURFACE space — what the agent reads: the rect within the
 *     screen's own viewport (so `surfaceSize` == the inspector
 *     viewport). No remap needed — the inspector rect IS surface space.
 */

/** Axis-aligned rect. The coordinate space is the caller's contract. */
export interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Clamp a rect to `[0,0]..[boundsWidth,boundsHeight]` (same space as
 *  the bounds). Returns null when nothing remains. */
export function clampRectToBounds(
  rect: SelectionRect,
  boundsWidth: number,
  boundsHeight: number,
): SelectionRect | null {
  const left = Math.max(0, rect.x);
  const top = Math.max(0, rect.y);
  const right = Math.min(boundsWidth, rect.x + rect.width);
  const bottom = Math.min(boundsHeight, rect.y + rect.height);
  if (right - left <= 0 || bottom - top <= 0) return null;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * Intersection of two rects (same coordinate space). Returns null when
 * they don't overlap (or the overlap is empty). Used by the parent
 * gap-drag marquee to clip a window-space selection to a device's
 * on-screen box.
 */
export function rectIntersection(a: SelectionRect, b: SelectionRect): SelectionRect | null {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right - left <= 0 || bottom - top <= 0) return null;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Area of the overlap between two rects (0 when they don't overlap). */
export function intersectionArea(a: SelectionRect, b: SelectionRect): number {
  const overlap = rectIntersection(a, b);
  return overlap ? overlap.width * overlap.height : 0;
}

/** A selectable device surface with its on-screen box (window px). */
export interface SurfaceBounds<T = unknown> {
  /** The caller's handle for this surface (e.g. the iframe element). */
  ref: T;
  /** The surface's on-screen rect in the same space as the marquee. */
  rect: SelectionRect;
}

/**
 * Given a marquee rect (window px) and the candidate device surfaces,
 * pick the surface the marquee overlaps MOST, clip the marquee to that
 * surface's bounds, and return both. Returns null when the marquee
 * touches no surface (drag stayed entirely in the gaps) — the caller
 * cancels silently. Ties (equal overlap area) resolve to the first
 * candidate, so caller order is the tiebreak.
 */
export function clipMarqueeToSurface<T>(
  marquee: SelectionRect,
  surfaces: Array<SurfaceBounds<T>>,
): { ref: T; clipped: SelectionRect } | null {
  let best: { ref: T; area: number } | null = null;
  for (const surface of surfaces) {
    const area = intersectionArea(marquee, surface.rect);
    if (area <= 0) continue;
    if (!best || area > best.area) best = { ref: surface.ref, area };
  }
  if (!best) return null;
  const target = surfaces.find((s) => s.ref === best!.ref)!;
  const clipped = rectIntersection(marquee, target.rect);
  if (!clipped) return null;
  return { ref: best.ref, clipped };
}

/**
 * Convert a rect that has already been clipped to `bounds` into
 * fractions of that box (each field in `[0,1]`). Resolution-independent,
 * so the inspector (which knows its own inner viewport) can multiply the
 * fractions back up without the parent needing the iframe's inner size.
 * Returns null when `bounds` is degenerate.
 */
export function rectToFractions(
  clipped: SelectionRect,
  bounds: SelectionRect,
): SelectionRect | null {
  if (bounds.width <= 0 || bounds.height <= 0) return null;
  return {
    x: (clipped.x - bounds.x) / bounds.width,
    y: (clipped.y - bounds.y) / bounds.height,
    width: clipped.width / bounds.width,
    height: clipped.height / bounds.height,
  };
}

/**
 * Map a rect from one coordinate space into another by uniform
 * per-axis scaling, rounding outward (floor origin / ceil far edge) so
 * the mapped rect never loses its last pixel row/column, then clamping
 * to the target bounds. Used inspector→display-box px for capture.
 */
export function scalePaneRect(
  rect: SelectionRect,
  fromWidth: number,
  fromHeight: number,
  targetWidth: number,
  targetHeight: number,
): SelectionRect | null {
  if (fromWidth <= 0 || fromHeight <= 0 || targetWidth <= 0 || targetHeight <= 0) return null;
  const sx = targetWidth / fromWidth;
  const sy = targetHeight / fromHeight;
  const left = Math.floor(rect.x * sx);
  const top = Math.floor(rect.y * sy);
  const right = Math.ceil((rect.x + rect.width) * sx);
  const bottom = Math.ceil((rect.y + rect.height) * sy);
  return clampRectToBounds(
    { x: left, y: top, width: right - left, height: bottom - top },
    targetWidth,
    targetHeight,
  );
}

/**
 * A DOM-element descriptor carried in an element selection. Mirrors
 * `ElementDescriptor` from `inspect-protocol.ts` but re-declared to
 * keep this pure module dependency-free. The agent localises via
 * `text` (its main grep key) + the crop — no scene-node id, since
 * created-game code is untouched.
 */
export interface SelectionElementInfo {
  tag: string;
  text: string;
  classes: string[];
  role?: string;
  ariaLabel?: string;
  src?: string;
}

/**
 * The structured selection attached to the chat message. `surface` is
 * which screen it came from (`tv` or `controller:N`); `rect` +
 * `surfaceSize` are in that screen's own viewport coordinates.
 */
export interface PreviewSelectionContext {
  kind: 'area' | 'element';
  surface: string;
  rect: SelectionRect;
  surfaceSize: { width: number; height: number };
  /** Present only for element selections. */
  element?: SelectionElementInfo;
  /** Visible text of the whole screen the selection came from, so the
   *  agent can identify which scene/state it is (Option A). */
  screenText?: string;
}

/**
 * Render the fenced context block prepended to the user's message at
 * send time. For element selections the agent greps the visible
 * `text`; for both, `rect`/`surfaceSize` say WHERE on which screen.
 */
export function formatSelectionContext(context: PreviewSelectionContext): string {
  const { rect, surfaceSize } = context;
  const lines = [
    '```crucible-selection',
    `kind: ${context.kind}`,
    `surface: ${context.surface}`,
    `rect: { x: ${rect.x}, y: ${rect.y}, w: ${rect.width}, h: ${rect.height} }`,
    `surfaceSize: ${surfaceSize.width}x${surfaceSize.height}`,
  ];
  if (context.kind === 'element' && context.element) {
    const el = context.element;
    lines.push(`element: <${el.tag}>`);
    if (el.text) lines.push(`text: ${JSON.stringify(el.text)}`);
    if (el.classes.length > 0) lines.push(`classes: ${el.classes.join(' ')}`);
    if (el.role) lines.push(`role: ${el.role}`);
    if (el.ariaLabel) lines.push(`ariaLabel: ${JSON.stringify(el.ariaLabel)}`);
    if (el.src) lines.push(`src: ${el.src}`);
    lines.push(
      'note: no scene-node id (the game DOM is not instrumented) — locate the source by ' +
        'grepping the visible text/label, then confirm against the attached crop.',
    );
  } else {
    lines.push('image: the attached screenshot crop shows exactly this region');
  }
  if (context.screenText) {
    lines.push(
      'screenText: visible text of the whole screen — use it to identify which scene/state ' +
        'this is, then grep for it to find the matching manifest:',
    );
    lines.push(`screenText: ${JSON.stringify(context.screenText)}`);
  }
  lines.push('```');
  return lines.join('\n');
}
